import type {
  KnowledgeNotebookDocument,
  KnowledgeNoteDocument,
  KnowledgeTabDocument,
  NoteBlock,
  NoteBlockKind,
} from '../types/note';

export const SYSTEM_TAB_IDS = {
  recent: '00000000-0000-4000-8000-000000000001',
  courses: '00000000-0000-4000-8000-000000000002',
  personal: '00000000-0000-4000-8000-000000000003',
  archive: '00000000-0000-4000-8000-000000000004',
} as const;

const SYSTEM_TABS: Array<{
  kind: keyof typeof SYSTEM_TAB_IDS;
  name: string;
  emoji: string;
  colorHex: string;
  sortOrder: number;
}> = [
  { kind: 'recent', name: '最近', emoji: '◷', colorHex: '#FF9600', sortOrder: -10 },
  { kind: 'courses', name: '课程', emoji: '▥', colorHex: '#1CB0F6', sortOrder: 0 },
  { kind: 'personal', name: '个人', emoji: '♙', colorHex: '#58CC02', sortOrder: 1 },
  { kind: 'archive', name: '归档', emoji: '▱', colorHex: '#9AA0A6', sortOrder: 2 },
];

export function createSystemTabs(now = new Date().toISOString()): KnowledgeTabDocument[] {
  return SYSTEM_TABS.map(tab => ({
    schemaVersion: 1,
    id: SYSTEM_TAB_IDS[tab.kind],
    name: tab.name,
    emoji: tab.emoji,
    colorHex: tab.colorHex,
    sortOrder: tab.sortOrder,
    kind: tab.kind,
    isSystem: true,
    createdAt: now,
    updatedAt: now,
  }));
}

export function createCustomTab(name = '新分区'): KnowledgeTabDocument {
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    id: crypto.randomUUID(),
    name,
    emoji: '🗂',
    colorHex: '#CE82FF',
    sortOrder: Date.now(),
    kind: 'custom',
    isSystem: false,
    createdAt: now,
    updatedAt: now,
  };
}

export function createNotebook(tabID: string, name = '新笔记本'): KnowledgeNotebookDocument {
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    id: crypto.randomUUID(),
    name,
    emoji: '📚',
    colorHex: '#58CC02',
    summary: '',
    sortOrder: Date.now(),
    isArchived: false,
    isPinned: false,
    location: { tabID },
    createdAt: now,
    updatedAt: now,
  };
}

export function createNote(location: {
  tabID?: string | null;
  notebookID?: string | null;
  parentNoteID?: string | null;
  courseID?: string | null;
}, title = '无标题笔记'): KnowledgeNoteDocument {
  const now = new Date().toISOString();
  return {
    schemaVersion: 3,
    id: crypto.randomUUID(),
    title,
    createdAt: now,
    updatedAt: now,
    isPinned: false,
    isArchived: false,
    archivedAt: null,
    sortOrder: Date.now(),
    location,
    blocks: [createBlock('paragraph')],
    childNoteIDs: [],
  };
}

export function createBlock(kind: NoteBlockKind): NoteBlock {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    orderIndex: 0,
    kind,
    text: '',
    checked: false,
    language: kind === 'code' ? 'swift' : null,
    indentLevel: 0,
    detailText: kind === 'toggle' || kind === 'code' ? '' : null,
    isExpanded: kind === 'toggle' ? true : null,
    simpleTable: kind === 'simpleTable'
      ? { schemaVersion: 1, rows: [['', ''], ['', '']] }
      : null,
    callout: kind === 'callout'
      ? { icon: '💡', colorHex: '#FFC800' }
      : null,
    backgroundStyle: null,
    canvasHeight: kind === 'sketch' ? 240 : null,
    assets: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function normalizeBlockOrder(blocks: NoteBlock[]): NoteBlock[] {
  return blocks.map((block, orderIndex) => ({ ...block, orderIndex }));
}
